import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { Location } from "@angular/common";
import {
  AnlageDetailDto,
  AnlagenClientProvider,
  FacilityMonitoringClientProvider,
  MonitoringBlackboxAssignToFacilityCommand,
  MonitoringBlackboxUnassignFromFacilityCommand,
  MonitoringBlackboxLookupDto,
  MonitoringStatusDto,
  EntityNotFoundMessage,
  LicensesClientProvider,
  MonitoringLicenseCountGetQuery,
} from "src/app/web-api/web-api";
import { ActivatedRoute } from "@angular/router";
import { NEVER, Observable, of, throwError } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import { WartungshistoryComponent } from "../../wartungshistory-timeline/wartungshistory.component";
import { UserService } from "src/app/services/user.service";
import { CstNavigationService } from "src/app/services/cst-navigation.service";
import { AnlageApiMonitoringBlackboxSelectDialogComponent } from "src/app/anlage-api/dialogs/monitoring-blackbox-select/monitoring-blackbox-select.component";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { AlertService } from "src/app/services";
import { Claims } from "src/app/constants";
import { CstRouteService, ICstRouteService } from "src/app/services/cst-route.service";
import { AnlageApiFaciliyUpdateDialogComponent, IAnlageApiFaciliyUpdateDialogData } from "src/app/anlage-api/dialogs/facility-update/facility-update.component";

class ElevatorsDetailPageUiState {
  private _storageKey: string = null;
  private _data: { layout: "layout-grid" | "layout-stream" } = {
    layout: "layout-grid",
  };
  public constructor(private _userService: UserService) {
    this._storageKey =
      this._userService?.CurrentUser?.mandantID ?? "" + "_" + this._storageKey;
    let json = JSON.parse(sessionStorage.getItem(this._storageKey));
    if (!json) {
      json = JSON.parse(localStorage.getItem(this._storageKey));
    }
    if (!json) {
      return;
    }
    Object.assign(this._data, json);
  }

  public get layout() {
    return this._data.layout;
  }
  public set layout(value: "layout-grid" | "layout-stream") {
    this._data.layout = value;
    this._Save();
  }

  private _Save() {
    const json = JSON.stringify(this._data);
    sessionStorage.setItem(this._storageKey, json);
    localStorage.setItem(this._storageKey, json);
  }
}

@Component({
    selector: "app-infoportal-api-elevators-detail",
    templateUrl: "./elevators-detail.component.html",
    styleUrls: ["./elevators-detail.component.scss", "../../styles.scss"],
    standalone: false
})
export class InfoportalApiElevatorDetailComponent implements OnInit {
  private _userService = inject(UserService);
  private _location = inject(Location);
  private _anlagenClient = inject(AnlagenClientProvider);
  private _licensesClient = inject(LicensesClientProvider);
  private _monitoringClient = inject(FacilityMonitoringClientProvider);
  private _navigation = inject(CstNavigationService);
  private _popup = inject(CstResponsivePopupService);
  private _dlgs = inject(CstDialogsService);
  private _alrt = inject(AlertService);

  private _routeService: ICstRouteService<"updateFacility">;
  public anlagenIDToDisplay: number = null;
  public data: AnlageDetailDto = null;
  public monitoringStatus: MonitoringStatusDto = null;
  public uiState: ElevatorsDetailPageUiState = null;
  facilityNoInternal = "";

  @ViewChild(WartungshistoryComponent, { static: false })
  private _historyComp: WartungshistoryComponent;

  constructor() {
    const route = inject(ActivatedRoute);
    const routeService = inject<CstRouteService<"updateFacility">>(CstRouteService);

    this.uiState = new ElevatorsDetailPageUiState(this._userService);
    route.paramMap.subscribe((params) => {
      this.anlagenIDToDisplay = +params.get("id"); // + um den string in eine number zu wandeln
      if (this.anlagenIDToDisplay) {
        this._LadeAnlage().subscribe();
      }
    });
    this._routeService = routeService.create(route);
    this._routeService.registerDialogTypeCallbacks({
      updateFacility: () => this._onFacilityUpdateOpenPopup(),
    });
  }

  public get user() {
    return this._userService?.CurrentUser;
  }

  public get isLinkedToBlackbox() {
    if (this.data?.monitoringBlackboxID) {
      return true;
    }
    return false;
  }

  ngOnInit() {
    if (!this.user?.internalAccess) {
      this.uiState.layout = "layout-stream";
    }
  }

  public isMonitoringSystemConnectVisible() {
    return (
      this._userService.HasClaim(Claims.IsInternal) &&
      !this.isMonitoringSystemDisconnectVisible()
    );
  }
  public isMonitoringSystemConnectEnabled() {
    return this.data && !this.isLinkedToBlackbox;
  }
  public onMonitoringSystemConnect() {
    this._licensesClient
      .getMonitoringLicenseCount(new MonitoringLicenseCountGetQuery())
      .pipe(
        switchMap((count) => {
          if (count.countAvailable === 0) {
            return this._dlgs
              .Information(
                $localize`:@@InfoPortal_ElevatorsDetail_BuMonitoringConnect_NoMoreLicensesInfo:There are no more monitoring licenses available!\r\nEither disconnect another system first, or order more monitoring licenses.`
              )
              .pipe(map((_) => NEVER));
          }
          const popRef = this._popup.open(
            AnlageApiMonitoringBlackboxSelectDialogComponent,
            {}
          );
          return popRef.afterClosed();
        }),
        switchMap((x: MonitoringBlackboxLookupDto) => {
          if (!(x instanceof MonitoringBlackboxLookupDto)) {
            return NEVER;
          }
          return this._dlgs
            .AskYesNo(
              $localize`:@@InfoPortal_ElevatorsDetail_BuMonitoringConnect_Question:Do you really want to connect this facility to the selected monitoring system?`
            )
            .pipe(
              switchMap((result) =>
                result === CstDialogResult.Yes ? of(x) : NEVER
              )
            );
        }),
        switchMap((x) => {
          const cmd = new MonitoringBlackboxAssignToFacilityCommand();
          cmd.init({ blackboxID: x.id, facilityID: this.data.anlageID });
          return this._monitoringClient.assignBlackboxToFacility(cmd).pipe(
            catchError((error) => {
              let message = "";
              if (error instanceof EntityNotFoundMessage) {
                message = `"${error.name}" mit ID "${error.key}" wurde nicht gefunden.`;
              } else {
                console.error(error);
                message = "Es ist ein unerwarteter Fehler aufgetreten.";
              }
              return this._dlgs
                .Information(message)
                .pipe(switchMap((_) => NEVER));
            })
          );
        }),
        tap((_) =>
          this._alrt.showSnackbar(
            $localize`:@@InfoPortal_ElevatorsDetail_BuMonitoringConnect_Success:Connect was successful`,
            { duration: "short" }
          )
        ),
        switchMap((_) => this._LadeAnlage())
      )
      .subscribe();
  }
  public isMonitoringSystemDisconnectVisible() {
    return (
      this._userService.HasClaim(Claims.IsInternal) &&
      this.data &&
      this.isLinkedToBlackbox
    );
  }
  public onMonitoringSystemDisconnect() {
    this._dlgs
      .AskYesNo(
        $localize`:@@InfoPortal_ElevatorsDetail_BuMonitoringDisconnect_Question:Do you really want to disconnect this facility from the selected monitoring system?`
      )
      .pipe(
        switchMap((result) =>
          result === CstDialogResult.Yes
            ? of(this.data.monitoringBlackboxID)
            : NEVER
        ),
        switchMap((x) => {
          const cmd = new MonitoringBlackboxUnassignFromFacilityCommand();
          cmd.init({ blackboxID: x, facilityID: this.data.anlageID });
          return this._monitoringClient.unassignBlackboxFromFacility(cmd);
        }),
        tap((_) =>
          this._alrt.showSnackbar(
            $localize`:@@InfoPortal_ElevatorsDetail_BuMonitoringDisconnect_Success:Disconnect was successful`,
            { duration: "short" }
          )
        ),
        switchMap((_) => this._LadeAnlage())
      )
      .subscribe();
  }

  public onFacilityUpdateRequest(){
      this._routeService.setQueryDialogType("updateFacility");
    }
    private _onFacilityUpdateOpenPopup() {
      const ref = this._popup.open<
        AnlageApiFaciliyUpdateDialogComponent,
        IAnlageApiFaciliyUpdateDialogData,
        AnlageDetailDto
      >(AnlageApiFaciliyUpdateDialogComponent, {
        data: { id: this.anlagenIDToDisplay },
      });
      return ref.afterClosed().pipe(
        tap((x) => {
          if (!x) {
            return;
          }
          this._LadeAnlage().subscribe();
        })
      );
    }

  private _LadeAnlage(): Observable<any> {
    this.data = null;
    this.facilityNoInternal = "";
    return this._anlagenClient.getSingle(this.anlagenIDToDisplay).pipe(
      catchError((err) => {
        if (err.status === 403) {
          this._navigation.navigateTo_Infoportal_Elevators();
        }
        return throwError(err);
      }),
      map((anlageDto) => {
        this.data = anlageDto;
        this.facilityNoInternal = this.data.anlageNrIntern;
        return anlageDto;
      }),
      switchMap((anlageDto) =>
        anlageDto.monitoringBlackboxID
          ? this._monitoringClient
              .getStatusSingle(anlageDto.monitoringBlackboxID)
              .pipe(tap((statusDto) => (this.monitoringStatus = statusDto)))
          : NEVER
      )
    );
  }

  public OnNavigateBackToList(): void {
    this._location.back();
  }

  public onFacilityNotesChanged() {
    if (!this._historyComp) {
      return;
    }
    this._historyComp.onSearchClick();
  }
}
